import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiExternalLink, FiDownload } from 'react-icons/fi';
import { API_BASE_URL } from '../../config';

/**
 * Workbook-style viewer for `GET /api/v1/markets-catalog/json`.
 *
 * Props:
 *  - scope?: string  ('all' | 'fictive' | 'real' | 'manual' | 'fictive,real' | …) default 'all'
 *  - showScopePicker?: boolean (default true)
 *  - height?: string | number — content height (default: '70vh')
 */
export default function CatalogJsonViewer({
  scope: initialScope = 'all',
  showScopePicker = true,
  height = '70vh',
}) {
  const [scope, setScope] = useState(initialScope);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [activeSheet, setActiveSheet] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr('');
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/markets-catalog/json?scope=${encodeURIComponent(scope)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!mounted) return;
        setData(json);
        setActiveSheet(0);
      } catch (e) {
        if (mounted) setErr(`Failed to load catalog JSON: ${e.message}`);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [scope]);

  const sheets = data?.sheets || [];
  const sheet = sheets[activeSheet];

  // Reset search when switching sheet
  useEffect(() => { setSearch(''); }, [activeSheet]);

  const filteredRows = useMemo(() => {
    if (!sheet) return [];
    const rows = sheet.kind === 'manual' ? (sheet.fields || []) : (sheet.rows || []);
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      Object.values(r).some(v => v != null && String(v).toLowerCase().includes(q))
    );
  }, [sheet, search]);

  const columns = useMemo(() => {
    if (!sheet) return [];
    if (sheet.kind === 'manual') return sheet.fieldsColumns || [];
    return sheet.columns || [];
  }, [sheet]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '4px 0 12px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        {showScopePicker && (
          <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Scope:
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                padding: '6px 10px',
                fontSize: '0.8rem',
              }}
            >
              <option value="all">All</option>
              <option value="fictive">Fictive only</option>
              <option value="real">Real only</option>
              <option value="manual">Manual only</option>
              <option value="fictive,real">Fictive + Real</option>
            </select>
          </label>
        )}

        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={sheet ? `Search in "${sheet.title}"…` : 'Search…'}
            disabled={!sheet}
            style={{
              width: '100%',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: 6,
              padding: '6px 10px 6px 30px',
              fontSize: '0.8rem',
            }}
          />
        </div>

        {data && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            v{data.version} • {new Date(data.generatedAt).toLocaleString()}
          </div>
        )}

        <a
          href={`${API_BASE_URL}/v1/markets-catalog/json?scope=${encodeURIComponent(scope)}`}
          target="_blank" rel="noreferrer"
          style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          Raw JSON <FiExternalLink size={12} />
        </a>
        <a
          href={`${API_BASE_URL}/v1/markets-catalog/export.xlsx?scope=${encodeURIComponent(scope)}`}
          target="_blank" rel="noreferrer"
          title="Admin only — requires auth-token header in production"
          style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <FiDownload size={12} /> XLSX
        </a>
      </div>

      {/* Sheet tabs */}
      {sheets.length > 0 && (
        <div style={{
          display: 'flex', gap: 4, padding: '10px 0',
          overflowX: 'auto',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {sheets.map((sh, idx) => (
            <button
              key={`${sh.kind}-${sh.title}-${idx}`}
              onClick={() => setActiveSheet(idx)}
              style={{
                background: idx === activeSheet ? 'var(--accent)' : 'transparent',
                color: idx === activeSheet ? '#0a0f1c' : 'var(--text-secondary)',
                border: '1px solid ' + (idx === activeSheet ? 'var(--accent)' : 'var(--border-color)'),
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: '0.78rem',
                fontWeight: idx === activeSheet ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {sh.title}
              <span style={{ marginLeft: 6, opacity: 0.7, fontSize: '0.7rem' }}>
                ({sh.kind === 'manual' ? (sh.fields?.length || 0) : (sh.rows?.length || 0)})
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 0' }}>
        {loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading…</div>}
        {err && <div style={{ color: '#e06c75', fontSize: '0.85rem' }}>{err}</div>}

        {!loading && !err && sheet && sheet.kind === 'manual' && (
          <ManualBlock sheet={sheet} />
        )}

        {!loading && !err && sheet && sheet.kind !== 'manual' && (
          <SheetTable columns={columns} rows={filteredRows} totalRows={(sheet.rows || []).length} />
        )}

        {!loading && !err && !sheet && (
          <div style={{ color: 'var(--text-muted)' }}>No sheets in response.</div>
        )}
      </div>
    </div>
  );
}

function ManualBlock({ sheet }) {
  return (
    <div>
      {sheet.description && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.55, marginTop: 0 }}>
          {sheet.description}
        </p>
      )}
      {sheet.endpoints && (
        <div style={{ margin: '12px 0' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
            Endpoints
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
            {Object.entries(sheet.endpoints).map(([k, v]) => (
              <li key={k} style={{ marginBottom: 4 }}>
                <strong style={{ color: 'var(--text-secondary)' }}>{k}:</strong>{' '}
                <code style={{ color: '#a8c1e8' }}>{v}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '14px 0 6px' }}>
        Fields
      </div>
      <SheetTable columns={sheet.fieldsColumns || []} rows={sheet.fields || []} totalRows={(sheet.fields || []).length} />
    </div>
  );
}

function SheetTable({ columns, rows, totalRows }) {
  if (!columns.length) return <div style={{ color: 'var(--text-muted)' }}>No columns.</div>;

  return (
    <>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8 }}>
        Showing {rows.length}{rows.length !== totalRows ? ` of ${totalRows}` : ''} row{rows.length === 1 ? '' : 's'}
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem',
          background: 'var(--bg-primary)',
        }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col} style={{
                  position: 'sticky', top: 0, zIndex: 1,
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderBottom: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                {columns.map(col => {
                  const v = row[col];
                  const isCode = col === 'code' || col === 'column' || col === 'groupCode' || col === 'periodCode' || col === 'statCode' || col === 'outcomeCode';
                  return (
                    <td key={col} style={{
                      padding: '6px 10px',
                      verticalAlign: 'top',
                      fontFamily: isCode ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
                      fontSize: isCode ? '0.74rem' : '0.78rem',
                      color: isCode ? '#a8c1e8' : 'var(--text-primary)',
                      whiteSpace: col === 'description' ? 'normal' : 'nowrap',
                      maxWidth: col === 'description' ? 420 : undefined,
                    }}>
                      {v == null || v === '' ? <span style={{ color: 'var(--text-muted)' }}>—</span> : String(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 16, color: 'var(--text-muted)', textAlign: 'center', fontSize: '0.82rem' }}>
                  No matching rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
